// Cross-device persistence of the dashboard's lecture selection (ora-fe
// ADR-0002: the request, `putLectureSelection`, lives in
// `src/services/lectures.ts`).
import type { ThunkAction, UnknownAction } from '@reduxjs/toolkit';
import { putLectureSelection } from '../../services/lectures';
import type { RootState } from '../../store/store';
import {
  fetchLectureTree,
  selectCurrentLectureExamId,
} from './lectureLoadThunks';
import type { LectureThunk } from './lectureThunks';

const LECTURE_SELECTION_PERSIST_DEBOUNCE_MS = 500;

// One debounce timer PER exam. A single shared timer would drop exam A's pending
// write if the user switched to exam B and edited it within the debounce window
// (the reschedule would cancel A's timer). Keying by exam keeps each exam's
// write independent.
const lectureSelectionPersistTimers = new Map<
  string,
  ReturnType<typeof setTimeout>
>();

// Debounced, cross-device persistence of the current lecture selection.
//
// The target exam and selection are captured at SCHEDULE time (not fire time)
// so a pending write for exam A still lands with A's selection even if the user
// switches to exam B before the debounce fires. Rapid edits to the same exam
// coalesce into a single PUT.
//
// CRITICAL: only dispatch this from genuine user selection mutations
// (toggle/select-all/clear). Never dispatch it from hydration or other
// programmatic selection changes, or entering a lectureless exam would persist
// an empty selection.
export const scheduleLectureSelectionPersist =
  (): ThunkAction<void, RootState, unknown, UnknownAction> =>
  (_dispatch, getState) => {
    const state = getState();
    const examId = state.lectures.lastFetchedExamId;
    if (!examId) return;
    // Snapshot now so a later exam switch can't retarget this write.
    const selectedLectureIds = [...state.lectures.selectedLectureIds];
    const existingTimer = lectureSelectionPersistTimers.get(examId);
    if (existingTimer) {
      clearTimeout(existingTimer);
    }

    const timer = setTimeout(() => {
      lectureSelectionPersistTimers.delete(examId);

      putLectureSelection(examId, selectedLectureIds).catch((error) => {
        // Optimistic UI already reflects the change; log and move on. The next
        // successful load re-hydrates from the backend source of truth.
        console.error('Failed to persist lecture selection', error);
      });
    }, LECTURE_SELECTION_PERSIST_DEBOUNCE_MS);

    lectureSelectionPersistTimers.set(examId, timer);
  };

/** Drop an exam's pending debounced selection write, if one is scheduled. */
const cancelPendingLectureSelectionPersist = (examId: string): void => {
  const timer = lectureSelectionPersistTimers.get(examId);
  if (!timer) return;

  clearTimeout(timer);
  lectureSelectionPersistTimers.delete(examId);
};

/**
 * Reload the current course's lecture selection after something outside the
 * dashboard changed it on the server — a confirmed companion lecture change or
 * its Undo (ORA-1159).
 *
 * The dashboard writes the WHOLE selection, so a debounced write scheduled
 * before the change would put the old set back. It is dropped before the
 * reload, and again once the reload lands so that a write scheduled from the
 * pre-reload selection while the request was in flight cannot win either.
 *
 * Nothing to do when no lecture state for the course is loaded: the dashboard
 * reads the server's selection on its next load.
 */
export const reloadLectureSelection =
  (): LectureThunk<void> => async (dispatch, getState) => {
    const state = getState();
    const examId = selectCurrentLectureExamId(state);
    if (!examId) return;

    const isCourseLoaded =
      state.lectures.lastFetchedExamId === examId ||
      state.lectureTree.examId === examId;

    if (!isCourseLoaded) return;

    cancelPendingLectureSelectionPersist(examId);

    try {
      // No `preserveSelection`: re-hydrating from the server's `selected` flags
      // is the point.
      await dispatch(
        fetchLectureTree({
          examId,
          skipLoadingState: true,
          onlyIfStillCurrent: true,
        })
      );
    } finally {
      cancelPendingLectureSelectionPersist(examId);
    }
  };
